import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { IFlexAction } from 'core-components/sitebuilder/models/flexGrid/IFlexAction'
import { IFlexLayout } from 'core-components/sitebuilder/models/flexGrid/IFlexLayout'
import { IApplicationData } from 'core-components/sitebuilder/models/IApplication'
import { ILayoutSettings } from 'core-components/sitebuilder/models/ILayoutSettings'
import { IRole } from 'core-components/sitebuilder/models/IRole'
import { ISitePage } from 'core-components/sitebuilder/models/ISitePage'
import { IUser } from 'core-components/sitebuilder/models/IUser'
import { IWidget } from 'core-components/sitebuilder/models/IWidget'
import { initialState } from 'core-components/sitebuilder/store/constants'
import { ISiteBuilderState } from 'core-components/sitebuilder/store/models/ISiteBuilderState'
import { IWidgetData } from 'core-components/sitebuilder/store/models/IWidgetData'

const siteBuilderSlice = createSlice({
  name: '@siteBuilder',
  initialState: initialState,
  reducers: {
    setApp(state, action: PayloadAction<IApplicationData>) {
      state.app = action.payload
    },
    setUser(state, action: PayloadAction<IUser>) {
      state.user = action.payload
    },
    setRoles(state, action: PayloadAction<IRole[]>) {
      state.roles = [...action.payload].sort((a, b) => a.order - b.order)
    },
    setCalledFlexWrapperAction(
      state,
      action: PayloadAction<ISiteBuilderState['calledFlexWrapperAction']>
    ) {
      state.calledFlexWrapperAction = action.payload
    },
    setCurrentSitePageId(state, action: PayloadAction<string>) {
      state.currentSitePageId = action.payload
    },
    setEditLayoutMode(state, action: PayloadAction<boolean>) {
      state.editLayoutMode = action.payload
    },
    setFlexSettings(state, action: PayloadAction<IFlexLayout>) {
      state.flexSettings = action.payload
    },
    addFlexAction(
      state,
      action: PayloadAction<{
        page: string
        area: string
        flexAction: IFlexAction
      }>
    ) {
      const { page, area, flexAction } = action.payload
      const getActions = () => state.flexActions[`${page}-${area || 0}`] || []

      let currentActions = getActions()

      const found = currentActions.find(
        (el) =>
          (el.cell1 === flexAction.cell1 && el.cell2 === flexAction.cell2) ||
          (el.cell1 === flexAction.cell2 && el.cell2 === flexAction.cell1)
      )
      if (found) {
        currentActions = currentActions.filter(
          (el) => !(el.cell1 === found.cell1 && el.cell2 === found.cell2)
        )
      }
      currentActions.push(flexAction)
      siteBuilderSlice.caseReducers.saveFlexAction(state, {
        ...action,
        payload: { page, area, flexActions: currentActions },
      })
    },
    saveFlexAction(
      state,
      action: PayloadAction<{
        page: string
        area: string
        flexActions: IFlexAction[]
      }>
    ) {
      const { page, area, flexActions } = action.payload
      const currentValue = state.flexActions[`${page}-${area}`] || []
      if (JSON.stringify(currentValue) !== JSON.stringify(flexActions)) {
        state.flexActions[`${page}-${area}`] = flexActions
        localStorage.setItem('sitebuilder__flexActions', JSON.stringify(state.flexActions))
      }
    },
    setFlexActions(state, action: PayloadAction<Record<string, IFlexAction[]>>) {
      state.flexActions = action.payload
    },
    setSitePages(state, action: PayloadAction<ISitePage[]>) {
      const sitePages = (structuredClone(action.payload) as ISitePage[]).sort((f, s) => {
        return f && s ? f.order - s.order : 0
      })
      const createMap = (items: ISitePage[]): { [key: string]: ISitePage } => {
        const result: { [key: string]: ISitePage } = {}
        items.forEach((item) => (result[item.id] = item))
        return result
      }

      const sitePageMap = createMap(sitePages)

      sitePages.forEach((sitePage) => {
        if (sitePageMap[sitePage.parent]) {
          sitePageMap[sitePage.parent].childrenIds = []
          sitePageMap[sitePage.parent].childrenVisibleIds = []
          sitePageMap[sitePage.parent].hasChildren = false
        }
      })

      sitePages.forEach((sitePage) => {
        if (sitePageMap[sitePage.parent]) {
          sitePageMap[sitePage.parent].childrenIds.push(sitePage.id)
          if (!sitePage.hidden) {
            sitePageMap[sitePage.parent].childrenVisibleIds.push(sitePage.id)
            sitePageMap[sitePage.parent].hasChildren = true
          }
        }
      })
      state.sitePages = sitePages
      state.sitePagesLoaded = true
    },
    setSitePagesSettings(state, action: PayloadAction<ILayoutSettings[]>) {
      state.sitePagesSettings = action.payload
    },
    setSitePagesLoaded(state, action: PayloadAction<boolean>) {
      state.sitePagesLoaded = action.payload
    },
    setWidgets(state, action: PayloadAction<IWidget[]>) {
      state.widgets = action.payload
    },
    setWidgetData(state, action: PayloadAction<Partial<IWidgetData>>) {
      const newValue = { ...state.widgetData, ...action.payload }
      if (JSON.stringify(newValue) !== JSON.stringify(state.widgetData)) {
        state.widgetData = newValue
      }
    },
    setRouteParams(state, action: PayloadAction<Record<string, string>>) {
      state.routeParams = action.payload
    },
  },
})

export const {
  setApp,
  setUser,
  setSitePages,
  setSitePagesSettings,
  setSitePagesLoaded,
  setCurrentSitePageId,
  setEditLayoutMode,
  addFlexAction,
  saveFlexAction,
  setFlexActions,
  setFlexSettings,
  setWidgets,
  setWidgetData,
  setRoles,
  setRouteParams,
  setCalledFlexWrapperAction,
} = siteBuilderSlice.actions

export default siteBuilderSlice.reducer
